<script lang="ts">
import { onMount } from 'svelte';
import { sanitizeRichTextHtml } from '$lib/utils/sanitise.js';
import { getServiceSectionAnchorIds } from '$lib/utils/servicePageSections.js';
import Link from '../../atoms/Link/Link.svelte';
import BlogCategoryNav from '../BlogCategoryNav/BlogCategoryNav.svelte';

let { services = [], hideBlock = false } = $props();

// Local state for tracking heights (not props to avoid reactivity issues)
let headerHeight = $state(0);
let subHeaderHeight = $state(0);

function updateheaderHeight(useRaf = false) {
    const measure = () => {
        const header = document.querySelector('.header') as HTMLElement | null;
        headerHeight = header?.offsetHeight || 0;

        // Always measure services-nav height (not just when stuck)
        // This is needed for mobile sticky positioning of inner items
        const servicesNavEl = document.querySelector('.services-nav') as HTMLElement | null;
        if (servicesNavEl) {
            subHeaderHeight = servicesNavEl.offsetHeight;
        }
    };

    if (useRaf) {
        // Use double rAF to ensure CSS transitions have completed
        requestAnimationFrame(() => requestAnimationFrame(measure));
    } else {
        measure();
    }
}

let serviceLinks = $derived(
    services.map((service) => {
        return {
            text: service.servicesHeading,
            to: `#${getServiceSectionAnchorIds(service.servicesHeading).canonical}`,
        };
    }),
);

let servicesNav = $state<Element | null>(null);
let stickyTriggerY: number = 0; /* size of final sticky padding-top */
let scrollY: number = $state(0);
let makeSticky: boolean = $state(false);

// Handle scroll-based sticky behaviour
$effect(() => {
    // Only track scrollY - don't call updateheaderHeight here to avoid infinite loops
    if (!servicesNav?.classList.contains('services-nav--is-stuck')) {
        stickyTriggerY =
            (servicesNav?.getBoundingClientRect().top || 0) +
            window.pageYOffset -
            ((document.querySelector('.header') as HTMLElement | null)?.offsetHeight ||
                0); /* size of final sticky padding-top */
    }

    const shouldBeSticky = scrollY > stickyTriggerY;

    // Only update state if it actually changed to prevent infinite loops
    if (shouldBeSticky !== makeSticky) {
        makeSticky = shouldBeSticky;
        // Recalculate header height when sticky state changes
        // (header has different height when stuck vs non-stuck on desktop)
        // Use rAF to ensure header CSS transition has completed before measuring
        updateheaderHeight(true);
    }
});

// Handle margin adjustment when sticky state changes
$effect(() => {
    const serviceGrid = document.querySelector('.service-grid') as HTMLElement | null;
    if (serviceGrid) {
        const expectedMargin = makeSticky ? `${subHeaderHeight}px` : '0px';
        if (serviceGrid.style.marginTop !== expectedMargin) {
            serviceGrid.style.marginTop = expectedMargin;
        }
    }
});

onMount(() => {
    updateheaderHeight();
    const handleResize = () => updateheaderHeight();
    window.addEventListener('resize', handleResize);

    return () => {
        window.removeEventListener('resize', handleResize);
    };
});
</script>

<svelte:window bind:scrollY />

{#if !hideBlock}
    <div class="services-nav" class:services-nav--is-stuck={makeSticky} bind:this={servicesNav} style="top:{headerHeight}px;">
        <BlogCategoryNav className="inpage-nav" links={serviceLinks} />
    </div>

    <div class="service-grid">
        {#each services as service, index (index)}
            {@const anchorIds = getServiceSectionAnchorIds(service.servicesHeading)}
            <div class="service-grid__item" data-aos="fade-up">
                <span class="anchor" id={anchorIds.canonical}></span>
                {#each anchorIds.aliases as alias (alias)}
                    <span class="anchor" id={alias}></span>
                {/each}
                <div class="service-grid__inner">
                    <div class="service-grid__inner-item" style="top:{headerHeight + subHeaderHeight}px;">
                        <div class="service-grid__stick" style="top:{headerHeight + subHeaderHeight}px;">
                            <h2>{service.servicesHeading}</h2>
                            <Link
                                to={service.contactEmailAddress}
                                type="link"
                                class="button button--inverse button--with-fixed-inverse-border service-grid__cta"
                            >
                                {#snippet text()}{service.ctaText}{/snippet}
                            </Link>
                        </div>
                    </div>
                    <div class="service-grid__inner-item">
                        {#each service.servicesList as serviceContent, index (index)}
                            <h3>{serviceContent.title}</h3>
                            <p>{serviceContent.description}</p>

                            <ul>
                                {#if serviceContent.subServices}
                                    {#each serviceContent.subServices.split('\n') as bullet, index (index)}
                                        <li>{@html sanitizeRichTextHtml(bullet)}</li>
                                    {/each}
                                {/if}
                            </ul>
                        {/each}
                    </div>
                </div>
            </div>
        {/each}
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/fonts';
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;
    .anchor {
        scroll-margin-top: 15rem;

        @include mq($from: tablet) {
            scroll-margin-top: 20rem;
        }
    }

    .services-nav {
        z-index: 11;
        &--is-stuck {
            position: fixed;
            top: 0;
            width: 100%;
            background: #fff;

            @include mq($from: tablet) {
                background: linear-gradient(to bottom, #fff 90%, transparent 100%);
            }
        }
    }

    .service-grid {
        display: grid;
        grid-template-columns: 1fr;
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        &__inner {
            display: grid;
            grid-gap: 2.5rem;
            grid-template-columns: 1fr;

            @include mq($from: tablet) {
                grid-gap: 5.5rem;
            }

            @include mq($from: desktop) {
                grid-template-columns: 50% 33%;
            }
        }

        &__stick {
            @include mq($from: tablet) {
                position: sticky;
                top: 0;
            }
        }

        &__item {
            margin-top: 5rem;
            padding-bottom: 2.4rem;

            &:not(:last-child) {
                @include mq($from: desktop) {
                    border-bottom: 1px solid var(--colour-jupiter);
                }
            }
        }

        &__inner-item:not(:last-child) {
            @include mq($until: tablet) {
                background: linear-gradient(to bottom, var(--colour-white) 90%, transparent 100%);
                position: sticky;
                top: calc(5rem + var(--announcement-bar-height, 0px));
            }
        }

        h2 {
            margin-bottom: 2rem;
            max-width: 50rem;
            @include typography.display-lg;
        }

        h3 {
            font-family: fonts.$fonts-secondary;
            font-size: 2.2rem;
            font-weight: fonts.$fonts-weight--normal;
            letter-spacing: -0.066rem;
            line-height: 1.1;
            margin-bottom: 1.5rem;
        }

        p {
            font-family: fonts.$fonts-secondary;
            font-size: 1.6rem;
            font-weight: fonts.$fonts-weight--normal;
            line-height: 1.3;
            margin-bottom: 2rem;
        }

        ul {
            font-family: fonts.$fonts-secondary;
            font-size: 1.6rem;
            font-weight: fonts.$fonts-weight--normal;
            margin: 0;
            padding: 0 0 5.5rem 0;

            @include mq($from: mobileLarge) {
                column-count: 2;
                padding: 0 0 6.5rem 0;
            }

            @include mq($from: desktop) {
                padding-bottom: 2.4rem;
            }

            li {
                padding: 1rem 0;
            }
        }
    }

    :global(.service-grid__cta) {
        display: none;

        @include mq($from: tablet) {
            display: inline-block;
        }
    }

    :global(.service-grid ul a) {
        color: var(--colour-black-out);
            text-decoration: underline;

        &:active,
        &:focus,
        &:hover {
            text-decoration: none;
        }
    }
</style>
